/**
 * The four hero tiles of the CI daily report.
 *
 * Each carries a split the manager cannot get from a single number. Pure
 * rendering — every figure comes off the report object, so the tiles cannot
 * disagree with the table or the call log below them.
 */

import { PacDailyReport, Ratio, ratioPercent } from '@bariendo/lib/pac-daily-report';
import { Group, Paper, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconCalendar, IconCheck, IconPhone, IconVideo } from '@tabler/icons-react';
import { ReactElement, ReactNode } from 'react';

/** An unknown percentage reads as an em dash, never `NaN%`. */
function formatPercent(ratio: Ratio): string {
  const percent = ratioPercent(ratio);
  return percent === undefined ? '—' : `${percent}%`;
}

function TileShell({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }): ReactElement {
  return (
    <Paper withBorder p="md" radius="md">
      <Group gap={6} mb="xs" c="dimmed">
        {icon}
        <Text size="xs" fw={500} tt="uppercase" style={{ letterSpacing: '0.04em' }}>
          {label}
        </Text>
      </Group>
      {children}
    </Paper>
  );
}

/**
 * A figure with its qualifier on the line *beneath* it, never trailing inline.
 * At 13px against a 24px number an inline unit reads loose, and a two-part unit
 * wraps badly in a tile this narrow.
 */
function Figure({ value, caption }: { value: string; caption: string }): ReactElement {
  return (
    <Stack gap={2}>
      <Text fz={24} fw={600} lh={1.1}>
        {value}
      </Text>
      <Text size="xs" c="dimmed">
        {caption}
      </Text>
    </Stack>
  );
}

export function HeroMetrics({ report }: { report: PacDailyReport }): ReactElement {
  const { inboundCalls, pickup, scheduledIntroCalls, consults, conversations } = report;

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
      {/* After-hours volume is a staffing decision, not a performance one —
          averaging the two hides both. */}
      <TileShell icon={<IconPhone size={14} />} label="Inbound calls">
        <Group grow align="flex-start">
          <Figure value={String(inboundCalls.businessHours)} caption="Business hours" />
          <Figure value={String(inboundCalls.afterHours)} caption="After hours" />
        </Group>
      </TileShell>

      {/* Business hours is what the team is accountable for; all hours is what
          the patient experiences. */}
      <TileShell icon={<IconCheck size={14} />} label="Pickup rate">
        <Stack gap="xs">
          <Group grow align="flex-start">
            <Figure value={formatPercent(pickup.businessHours)} caption="Business hours" />
            <Figure value={formatPercent(pickup.allHours)} caption="All hours" />
          </Group>
          <Text size="xs" c="dimmed">
            {pickup.allHours.numerator} of {pickup.allHours.denominator} routed
          </Text>
        </Stack>
      </TileShell>

      {/* A booked call that no-shows is not a conversation. */}
      <TileShell icon={<IconVideo size={14} />} label="Scheduled intro call">
        <Figure
          value={`${scheduledIntroCalls.shown} of ${scheduledIntroCalls.booked}`}
          caption={
            scheduledIntroCalls.noShows > 0
              ? `Showed · ${scheduledIntroCalls.noShows} no-show${scheduledIntroCalls.noShows === 1 ? '' : 's'}`
              : 'Showed'
          }
        />
      </TileShell>

      {/* Split by source because the two convert very differently: a blended
          number hides that scheduling is the higher-yield motion. */}
      <TileShell icon={<IconCalendar size={14} />} label="Consults scheduled">
        <Stack gap="xs">
          <Figure
            value={`${consults.total.numerator} of ${consults.total.denominator}`}
            caption={`Conversations · ${formatPercent(consults.total)}`}
          />
          <Group grow align="flex-start">
            <Stack gap={2}>
              <Text fz={18} fw={600} lh={1.1}>
                {formatPercent(consults.fromInbound)}
              </Text>
              <Text size="xs" c="dimmed">
                {consults.fromInbound.numerator} of {consults.fromInbound.denominator} inbound
              </Text>
            </Stack>
            <Stack gap={2}>
              <Text fz={18} fw={600} lh={1.1}>
                {formatPercent(consults.fromScheduled)}
              </Text>
              <Text size="xs" c="dimmed">
                {consults.fromScheduled.numerator} of {consults.fromScheduled.denominator} scheduled
              </Text>
            </Stack>
          </Group>
          {conversations === 0 && (
            <Text size="xs" c="dimmed">
              No conversations yet
            </Text>
          )}
        </Stack>
      </TileShell>
    </SimpleGrid>
  );
}
